/**
 * Tab focus trap for a hand-rolled dialog.
 *
 * `aria-modal="true"` asserts that everything outside the dialog is inert, but
 * the browser does not enforce it: without a trap, Tab walks out of the dialog
 * onto controls hidden behind the backdrop, which a sighted keyboard user cannot
 * see. Shared so the nested adjustment dialogs behave the same way — an overlay
 * that traps and one that does not is worse than either.
 */
export const trapTabWithin = (
  container: HTMLElement | null,
  event: { key: string; shiftKey: boolean; preventDefault: () => void }
): void => {
  if (event.key !== 'Tab' || !container) return;
  // `[tabindex="-1"]` is excluded from EVERY branch, not just the bare-tabindex
  // one: a combobox drives its options with aria-activedescendant and takes them
  // out of the tab order that way, and counting them here would put `first` /
  // `last` on elements Tab can never reach — the wrap would then jump to a
  // non-focusable node and drop focus out of the dialog.
  const focusables = container.querySelectorAll<HTMLElement>(
    'button:not([disabled]):not([tabindex="-1"]), ' +
      'input:not([disabled]):not([tabindex="-1"]), ' +
      '[href]:not([tabindex="-1"]), ' +
      'select:not([disabled]):not([tabindex="-1"]), ' +
      'textarea:not([disabled]):not([tabindex="-1"]), ' +
      '[tabindex]:not([tabindex="-1"])'
  );
  if (focusables.length === 0) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement;
  if (event.shiftKey && (active === first || active === container)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
};
